import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Envelope } from "@phosphor-icons/react";

function Field({ label, testid, value, onChange, placeholder, type = "text" }) {
  return (
    <div>
      <label className="label-eyebrow text-neutral-700">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
      />
    </div>
  );
}

export default function SettingsEmail() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  const [rules, setRules] = useState(null);
  const [catalog, setCatalog] = useState({});
  const [savingRules, setSavingRules] = useState(false);

  useEffect(() => {
    api.get("/admin/email/config").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/email/notifications").then((r) => {
      setRules(r.data.rules || {});
      setCatalog(r.data.catalog || {});
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (payload.password === "••••••••") delete payload.password;
      const { data } = await api.post("/admin/email/config", payload);
      setCfg(data);
      toast.success("Impostazioni SMTP salvate");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo.trim() || !testTo.includes("@")) { toast.error("Indirizzo email non valido"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/email/test", { to: testTo.trim() });
      toast.success(`Email di test inviata (${data.provider})`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setTesting(false); }
  };

  const saveRules = async () => {
    setSavingRules(true);
    try {
      const { data } = await api.post("/admin/email/notifications", { rules });
      setRules(data.rules || {});
      toast.success("Regole notifiche salvate");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingRules(false); }
  };

  const updateRule = (evt, patch) => setRules((r) => ({ ...r, [evt]: { ...r[evt], ...patch } }));

  if (!cfg) return null;

  return (
    <section className="border border-neutral-200 bg-white" data-testid="smtp-section">
      <div className="p-4 md:p-6 border-b border-neutral-200 flex items-start gap-3">
        <Envelope size={22} weight="bold" className="text-[#002FA7] mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="label-eyebrow mb-1">Notifiche Email (SMTP)</div>
          <h3 className="font-display font-bold text-lg tracking-tight">Server SMTP + regole di invio</h3>
          <p className="text-sm text-neutral-600 mt-1">
            Configura il server SMTP per l'invio automatico di notifiche (backup, richieste di acquisto, movimenti firmati). Se non configurato, il sistema usa Resend come fallback.
          </p>
        </div>
      </div>

      <div className="p-4 md:p-6 space-y-6">
        {/* Connection form */}
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="smtp-enabled" checked={cfg.enabled || false}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              className="w-4 h-4 accent-[#002FA7]" />
            <span className="font-medium">Notifiche email attive</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Server SMTP (host)" testid="smtp-host" value={cfg.host}
              onChange={(v) => setCfg({ ...cfg, host: v })} placeholder="smtppro.zoho.eu" />
            <Field label="Porta" testid="smtp-port" type="number" value={cfg.port}
              onChange={(v) => setCfg({ ...cfg, port: parseInt(v, 10) || 587 })} placeholder="587" />

            <div>
              <label className="label-eyebrow text-neutral-700">Sicurezza</label>
              <select data-testid="smtp-security" value={cfg.security}
                onChange={(e) => setCfg({ ...cfg, security: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                <option value="starttls">STARTTLS (porta 587)</option>
                <option value="ssl">SSL/TLS (porta 465)</option>
                <option value="none">Nessuna (non consigliato)</option>
              </select>
            </div>
            <Field label="Email destinatario di default" testid="smtp-default-to" value={cfg.default_to}
              onChange={(v) => setCfg({ ...cfg, default_to: v })} placeholder="info@powercom.srl" />

            <Field label="Username" testid="smtp-username" value={cfg.username}
              onChange={(v) => setCfg({ ...cfg, username: v })} placeholder="info@powercom.srl" />
            <Field label="Password (lascia vuoto per non modificare)" testid="smtp-password" type="password" value={cfg.password}
              onChange={(v) => setCfg({ ...cfg, password: v })} placeholder="••••••••" />

            <Field label="Mittente (from email)" testid="smtp-from-email" value={cfg.from_email}
              onChange={(v) => setCfg({ ...cfg, from_email: v })} placeholder="info@powercom.srl" />
            <Field label="Nome mittente" testid="smtp-from-name" value={cfg.from_name}
              onChange={(v) => setCfg({ ...cfg, from_name: v })} placeholder="Powercom Magazzino" />
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button data-testid="smtp-save" disabled={saving} onClick={save}
              className="px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40">
              {saving ? "Salvo…" : "Salva impostazioni"}
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-[250px]">
              <input data-testid="smtp-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                placeholder="Email destinatario del test"
                className="flex-1 px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <button data-testid="smtp-test-btn" disabled={testing} onClick={sendTest}
                className="px-4 py-2 border border-neutral-300 hover:bg-neutral-100 disabled:opacity-40">
                {testing ? "Invio…" : "Invia email di prova"}
              </button>
            </div>
          </div>
        </div>

        {/* Notification rules */}
        {rules && Object.keys(rules).length > 0 && (
          <div className="pt-4 border-t border-neutral-200">
            <div className="label-eyebrow mb-2">Regole notifiche per evento</div>
            <p className="text-xs text-neutral-600 mb-3">
              Per ogni evento del sistema decidi se inviare l'email e a chi. Puoi inserire più indirizzi separati da virgola.
              Il token speciale <code className="px-1 bg-neutral-100">__requester__</code> viene sostituito con l'email del richiedente (utile per le richieste di acquisto);
              il token <code className="px-1 bg-neutral-100">__customer__</code> con l'email del cliente destinatario (per i DDT).
            </p>
            <div className="border border-neutral-200 overflow-x-auto" data-testid="notification-rules">
              <table className="min-w-full text-sm">
                <thead className="bg-neutral-50 text-neutral-600 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="px-3 py-2 text-left">Attiva</th>
                    <th className="px-3 py-2 text-left">Evento</th>
                    <th className="px-3 py-2 text-left">Destinatari (separati da virgola)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(catalog).map(([evt, meta]) => {
                    const r = rules[evt] || { enabled: true, recipients: [] };
                    return (
                      <tr key={evt} className="border-t border-neutral-200">
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            data-testid={`rule-enabled-${evt}`}
                            checked={r.enabled}
                            onChange={(e) => updateRule(evt, { enabled: e.target.checked })}
                            className="w-4 h-4 accent-[#002FA7]"
                          />
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="font-medium">{meta.label}</div>
                          <div className="text-xs text-neutral-500 font-mono">{evt}</div>
                        </td>
                        <td className="px-3 py-2">
                          <input
                            data-testid={`rule-recipients-${evt}`}
                            value={(r.recipients || []).join(", ")}
                            onChange={(e) => updateRule(evt, {
                              recipients: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                            })}
                            placeholder="email@example.com, __requester__"
                            className="w-full px-2 py-1 border border-neutral-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="mt-3">
              <button data-testid="rules-save" disabled={savingRules} onClick={saveRules}
                className="px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40">
                {savingRules ? "Salvo…" : "Salva regole notifiche"}
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
